import React, { useEffect, useState } from 'react';
import './ProductEdit.css';
import GeneralSelect from '../../../main/components/select/GeneralSelect';
import GeneralInput from '../../../main/components/generalInput/GeneralInput';
import { useForm } from 'react-hook-form';
import IProduct from '../../../main/interfaces/IProduct';
import IProductCategory from '../../../main/interfaces/IProductCategory';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import { toast } from 'react-toastify';
import axiosInstance from '../../../main/initializers/axios';
import eFormType from '../../../main/assets/enums/eFormType';
import useGetEnums from '../../../main/hooks/useGetEnums/index';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import { CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { compressImageIfNeeded } from '../../../main/utils/imageCompression';

interface ProductStockSnapshot {
  id: number;
  productId: number;
  quantity: number;
  minQuantity: number;
  buyingPrice: number;
  sellingPrice: number;
}

interface EditProductProps {
  tempProduct?: IProduct;
  modalType: eFormType;
  onSubmitData: () => void;
  createOrUpdate: boolean;
}

const ProductEdit: React.FC<EditProductProps> = ({ tempProduct, modalType, onSubmitData, createOrUpdate }) => {
  const BOTTLE_UNIT_ID = 16;

  const [categories, setCategories] = useState<IProductCategory[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [configId, setConfigId] = useState<number>(0);
  const [unitsPerStock, setUnitsPerStock] = useState<number>(1);
  const [currentUnitsLeft, setCurrentUnitsLeft] = useState<number>(1);
  const [isConfigActive, setIsConfigActive] = useState<boolean>(true);
  const [isConfigLoading, setIsConfigLoading] = useState<boolean>(false);

  const allEnums = useGetEnums();
  const { i18n } = useTranslation();

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<IProduct>({
    defaultValues: {
      id: modalType === eFormType.Edit ? tempProduct?.id : 0,
      name: modalType === eFormType.Edit ? tempProduct?.name : '',
      code: modalType === eFormType.Edit ? tempProduct?.code : '',
      price: modalType === eFormType.Edit ? tempProduct?.price : 0,
      unitId: modalType === eFormType.Edit ? tempProduct?.unitId : 0,
      isActive: modalType === eFormType.Edit ? tempProduct?.isActive : false,
      isEditable: modalType === eFormType.Edit ? tempProduct?.isEditable : false,
      categoryId: modalType === eFormType.Edit ? tempProduct?.categoryId : 0,
    },
  });

  const [file, setFile] = useState<File | null>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const productId = watch('id') || tempProduct?.id || 0;
  const [stockSnapshot, setStockSnapshot] = useState<ProductStockSnapshot>({
    id: 0,
    productId: 0,
    quantity: 0,
    minQuantity: 0,
    buyingPrice: 0,
    sellingPrice: 0,
  });

  // ✅ Load stockQty
  useEffect(() => {
    if (!productId) {
      setStockSnapshot({ id: 0, productId: 0, quantity: 0, minQuantity: 0, buyingPrice: 0, sellingPrice: 0 });
      return;
    }

    axiosInstance
      .get(ApiConstants.stock.list)
      .then((res) => {
        const stockItems = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.items) ? res.data.items : [];
        const selectedStock = stockItems.find((item: any) => Number(item?.productId ?? item?.ProductId ?? 0) === Number(productId));

        setStockSnapshot({
          id: Number(selectedStock?.id ?? selectedStock?.Id ?? 0),
          productId: Number(selectedStock?.productId ?? selectedStock?.ProductId ?? productId),
          quantity: Number(selectedStock?.quantity ?? selectedStock?.Quantity ?? 0),
          minQuantity: Number(selectedStock?.minQuantity ?? selectedStock?.MinQuantity ?? 0),
          buyingPrice: Number(selectedStock?.buyingPrice ?? selectedStock?.BuyingPrice ?? 0),
          sellingPrice: Number(selectedStock?.sellingPrice ?? selectedStock?.SellingPrice ?? 0),
        });
      })
      .catch(() => setStockSnapshot({ id: 0, productId: Number(productId), quantity: 0, minQuantity: 0, buyingPrice: 0, sellingPrice: 0 }));
  }, [productId]);
  const selectedUnitId = Number(watch('unitId') ?? 0);
  const isBottleUnit = selectedUnitId === BOTTLE_UNIT_ID;

  useEffect(() => {
    axiosInstance
      .get(ApiConstants.Products.productCategory)
      .then((res) => {
        const raw = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.items) ? res.data.items : [];
        const normalized = raw.map((c: any) => ({
          ...c,
          id: c?.id ?? c?.Id ?? 0,
          name: c?.name ?? c?.Name ?? '',
        }));
        setCategories(normalized);
      })
      .catch(() => setCategories([]));

    fetchImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🔹 Kur prindi pulson createOrUpdate, bëjmë submit
  useEffect(() => {
    if (!createOrUpdate) return;

    const wrappedSubmit = handleSubmit(async (formData) => {
      const ok = await onSubmit(formData);
      if (!ok) return;
      onSubmitData();
    });

    wrappedSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOrUpdate]);

  // ✅ Bottle configuration
  useEffect(() => {
    if (!isBottleUnit) return;

    if (!productId) {
      setConfigId(0);
      setUnitsPerStock((prev) => (prev > 0 ? prev : 1));
      setCurrentUnitsLeft((prev) => (prev >= 0 ? prev : 0));
      setIsConfigActive(true);
      return;
    }

    let isMounted = true;

    const loadConfiguration = async () => {
      setIsConfigLoading(true);
      try {
        const res = await axiosInstance.get(`${ApiConstants.productConfiguration.byProduct}?productId=${productId}`);
        if (!isMounted) return;

        const cfg = res.data ?? {};
        const loadedUnits = Number(cfg?.unitsPerStock ?? cfg?.UnitsPerStock ?? 1);
        const loadedCurrent = Number(cfg?.currentUnitsLeft ?? cfg?.CurrentUnitsLeft ?? loadedUnits);

        setConfigId(Number(cfg?.id ?? cfg?.Id ?? 0));
        setUnitsPerStock(loadedUnits > 0 ? loadedUnits : 1);
        setCurrentUnitsLeft(loadedCurrent >= 0 ? loadedCurrent : 0);
        setIsConfigActive(Boolean(cfg?.isActive ?? cfg?.IsActive ?? true));
      } catch (error: any) {
        if (error?.response?.status !== 404) toast.error(i18n.t('errors.generalError'));
        if (isMounted) {
          setConfigId(0);
          setUnitsPerStock(1);
          setCurrentUnitsLeft(1);
          setIsConfigActive(true);
        }
      } finally {
        if (isMounted) setIsConfigLoading(false);
      }
    };

    loadConfiguration();
    return () => {
      isMounted = false;
    };
  }, [isBottleUnit, productId, i18n]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;
    const selectedFile = e.target.files[0];
    const compressedFile = await compressImageIfNeeded(selectedFile, { maxKB: 200 });
    setFile(compressedFile);

    const reader = new FileReader();
    reader.onloadend = () => setPreviewUrl(reader.result as string);
    reader.readAsDataURL(compressedFile);
  };

  const onSubmit = async (data: IProduct): Promise<boolean> => {
    setIsLoading(true);

    const params = {
      id: data.id,
      name: data.name,
      code: data.code,
      price: data.price,
      unitId: Number(data.unitId),
      isActive: data.isActive,



      isEditable: data.isEditable,
      categoryId: Number(data.categoryId),
    };

    try {
      const response = await axiosInstance.post(ApiConstants.Products.update, params);

      if (response.status === 200) {
        const savedProductId = Number(response.data?.id ?? response.data?.Id ?? response.data?.data?.id ?? data.id ?? productId ?? 0);

        await axiosInstance.post(ApiConstants.stock.update, {
          Id: Number(stockSnapshot.id ?? 0),
          ProductId: savedProductId,
          Quantity: Number(stockSnapshot.quantity ?? 0),
          BuyingPrice: Number(stockSnapshot.buyingPrice ?? 0),
          MinQuantity: Number(stockSnapshot.minQuantity ?? 0),
          SellingPrice: Number(stockSnapshot.sellingPrice ?? 0),
        });

        if (isBottleUnit) {
          const ups = Number(unitsPerStock);
          const cul = Number(currentUnitsLeft);

          if (!Number.isFinite(ups) || ups <= 0) {
            toast.error('Units per stock duhet te jete > 0');
            return false;
          }
          if (!Number.isFinite(cul) || cul < 0 || cul > ups) {
            toast.error('Current units left duhet te jete mes 0 dhe Units per stock');
            return false;
          }
          if (savedProductId <= 0) {
            toast.error(i18n.t('errors.generalError'));
            return false;
          }

          const cfgRes = await axiosInstance.post(ApiConstants.productConfiguration.save, {
            id: configId,
            productId: savedProductId,
            unitsPerStock: ups,
            currentUnitsLeft: cul,
            isActive: isConfigActive,
          });
          setConfigId(Number(cfgRes?.data?.id ?? cfgRes?.data?.Id ?? configId));
        }

        toast.success(
          modalType === eFormType.New
            ? `${i18n.t('generic.product')} ${i18n.t('orderDetails.createdSuccessfully')}`
            : `${i18n.t('generic.product')} ${i18n.t('umb.updatedSuccessfully')}`,
        );
        return true;
      }

      toast.error(i18n.t('generic.somethingWrong'));
      return false;
    } catch (error) {
      console.error(error);
      toast.error(i18n.t('errors.generalError'));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitFile = async () => {
    if (!file) {
      toast.warning(i18n.t('product.selectFileWarning'));
      return;
    }
    if (!productId) {
      toast.warning(i18n.t('product.productSaveBeforeImageWarning'));
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('EntityId', productId.toString());
    formData.append('EntityType', ApiConstants.EntityTypes.Product.toString());

    try {
      await axiosInstance.post(ApiConstants.file.upload, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(i18n.t('product.imageUploadSuccess'));
      fetchImage();
      setPreviewUrl(null);
    } catch (error) {
      console.error(i18n.t('product.imageFetchError'), error);
      toast.error(i18n.t('product.imageUploadFailed'));
    } finally {
      setIsUploading(false);
    }
  };

  const fetchImage = async () => {
    try {
      if (productId) {
        setImageSrc(ApiConstants.file.downloadUrl(productId, ApiConstants.EntityTypes.Product));
      }
    } catch (error) {
      console.error(i18n.t('product.imageUploadFailed'), error);
    }
  };

  const removeImage = () => {
    setFile(null);
    setPreviewUrl(null);
  };

  const measurementUnitKeyMap: Record<string, string> = {
    PCS: 'Piece',
    Piece: 'Piece',
    KiloGram: 'Kilogram',
    Kilogram: 'Kilogram',
    KG: 'Kilogram',
    Gram: 'Gram',
    GR: 'Gram',
    Liter: 'Liter',
    LITER: 'Liter',
    BasedOnTemp: 'BasedOnTemp',
    BOX: 'Box',
    PAKO: 'Pack',
    HOUR: 'Hour',
    DAY: 'Day',
    M2: 'SquareMeter',
    M3: 'CubicMeter',
    VIT: 'Year',
    AMPULE: 'Ampoule',
    KAPSULE: 'Capsule',
    NJE: 'Unit',
    SHISHE: 'Bottle',
  };

  const humanizeEnumLabel = (rawName?: string) => {
    if (!rawName) return '';
    const withSpaces = rawName
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .trim();
    if (!withSpaces) return rawName;
    if (/^[A-Z0-9 ]+$/.test(withSpaces)) return withSpaces.toLowerCase().replace(/\b\w/g, (ch) => ch.toUpperCase());
    return withSpaces;
  };

  const translatedMeasurementUnits =
    allEnums.MeasurementUnit?.map((u: any) => {
      const normalizedKey = measurementUnitKeyMap[u.name] ?? u.name;
      const translated = i18n.t(`measurementUnit.${normalizedKey}`, { defaultValue: '' });
      const translatedFromRaw = i18n.t(`measurementUnit.${u.name}`, { defaultValue: '' });
      return { ...u, translatedName: translated || translatedFromRaw || humanizeEnumLabel(u.name) };
    }) ?? [];

  return (
    <div className="product-edit-container">
      <div className="product-edit-header">
        <h2 className="product-edit-title">
          {modalType === eFormType.New ? `${i18n.t('product.addNewProduct')} ` : ` ${i18n.t('generic.edit')}\n          `}
        </h2>
        <p className="product-edit-subtitle">
          {modalType === eFormType.New ? i18n.t('product.editSubtitleNew') : i18n.t('product.editSubtitleEdit')}{' '}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="product-edit-form">
        <div className="product-edit-grid">
          {/* Left Column - Image Upload */}
          <div className="product-image-section">
            <div className="image-upload-card">
              <h3 className="section-title">{i18n.t('product.imageSectionTitle')}</h3>

              <div className="image-preview-container">
                {previewUrl ? (
                  <div className="image-preview-wrapper">
                    <img src={previewUrl} alt="Preview" className="image-preview" />
                    <button type="button" className="remove-image-btn" onClick={removeImage}>
                      <CloseIcon />
                    </button>
                  </div>
                ) : imageSrc ? (
                  <img src={imageSrc} alt="Product" className="product-image" />
                ) : (
                  <div className="image-placeholder">
                    <AddPhotoAlternateIcon className="placeholder-icon" />
                    <p>{i18n.t('product.noImageUploaded')}</p>
                  </div>
                )}
              </div>

              <div className="upload-controls">
                <div className="file-input-wrapper">
                  <input type="file" id="file-upload" onChange={handleFileChange} accept="image/*" className="file-input" />
                  <label htmlFor="file-upload" className="file-input-label">
                    <AddPhotoAlternateIcon />
                    <span>{i18n.t('product.chooseImage')}</span>
                  </label>
                </div>

                {productId !== 0 && file && (
                  <button type="button" onClick={handleSubmitFile} className="upload-btn" disabled={isUploading}>
                    {isUploading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <>
                        <CloudUploadIcon />
                        <span>{i18n.t('product.upload')}</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              <p className="upload-hint">{i18n.t('product.uploadHint')}</p>
            </div>
          </div>

          {/* Right Column - Form Fields */}
          <div className="product-form-section">
            <div className="form-section-card">
              <h3 className="section-title">{i18n.t('product.detailsSectionTitle')}</h3>

              <div className="form-grid">
                <div className="form-group">
                  <GeneralInput
                    required
                    label={i18n.t('product.nameLabel')}
                    name="name"
                    type="text"
                    placeholder={i18n.t('product.namePlaceholder')}
                    errors={errors}
                    control={control}
                  />
                </div>

                <div className="form-group">
                  <GeneralInput
                    required
                    label={i18n.t('product.codeLabel')}
                    name="code"
                    type="text"
                    placeholder={i18n.t('product.codePlaceholder')}
                    errors={errors}
                    control={control}
                  />
                </div>

                <div className="form-group">
                  <GeneralSelect
                    setSelectedVal={(val) => console.log(val)}
                    data={categories}
                    required
                    control={control}
                    errors={errors}
                    label={i18n.t('product.categoryLabel')}
                    name="categoryId"
                    value="name"
                  />
                </div>

                <div className="form-group">
                  <GeneralSelect
                    preSelectedVal={i18n.t('product.unitPlaceholder')}
                    setSelectedVal={(val) => console.log(val)}
                    data={translatedMeasurementUnits}
                    required
                    errors={errors}
                    control={control}
                    label={i18n.t('product.unitLabel')}
                    name="unitId"
                    value="translatedName"
                  />
                </div>

                {isBottleUnit ? (
                  <>
                    <div className="form-group">
                      <label className="giLabel">Units per stock</label>
                      <input
                        className="giInput"
                        type="number"
                        min={1}
                        step={1}
                        value={unitsPerStock}
                        disabled={isConfigLoading}
                        onChange={(e) => {
                          const nextUnits = Math.max(1, Number(e.target.value) || 1);
                          setUnitsPerStock(nextUnits);
                          if (currentUnitsLeft > nextUnits) setCurrentUnitsLeft(nextUnits);
                        }}
                      />
                      <small className="pcHint">Sa gota/njesi nxjerr nje shishe e plote.</small>
                    </div>

                    <div className="form-group">
                      <label className="giLabel">Current units left</label>
                      <input
                        className="giInput"
                        type="number"
                        min={0}
                        step={1}
                        max={Math.max(1, unitsPerStock)}
                        value={currentUnitsLeft}
                        disabled={isConfigLoading}
                        onChange={(e) => {
                          const next = Math.max(0, Math.min(unitsPerStock, Number(e.target.value) || 0));
                          setCurrentUnitsLeft(next);
                        }}
                      />
                      <small className="pcHint">Sa njesi kane mbetur ne shishen aktuale.</small>
                    </div>
                  </>
                ) : null}

                <div className="form-group">
                  <GeneralInput
                    required
                    label={i18n.t('product.priceLabel')}
                    name="price"
                    type="number"
                    placeholder={i18n.t('product.pricePlaceholder')}
                    errors={errors}
                    control={control}
                  />
                </div>              </div>
            </div>

            <div className="form-section-card">
              <h3 className="section-title">{i18n.t('generic.settings')}</h3>

              <div className="settings-grid">
                <div className="checkbox-group">
                  <GeneralInput
                    required={false}
                    label={i18n.t('product.isEditableFieldLabel')}
                    name="isEditable"
                    type="checkbox"
                    placeholder=""
                    errors={errors}
                    control={control}
                  />
                  <span className="checkbox-label">{i18n.t('product.isEditableLabel')}</span>
                </div>

                <div className="checkbox-group">
                  <GeneralInput
                    type="checkbox"
                    required={false}
                    label={i18n.t('product.isActiveFieldLabel')}
                    name="isActive"
                    placeholder=""
                    errors={errors}
                    control={control}
                  />
                  <span className="checkbox-label">{i18n.t('product.isActiveLabel')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProductEdit;

